import { configureStore } from '@reduxjs/toolkit';
import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';

// Imports des slices
import modalReducer from '../features/modals/modalSlice';
import noteReducer from '../features/notes/noteSlice';
import noteHistoryReducer from '../features/notes/noteHistorySlice';
import noteBacklinksReducer from '../features/notes/noteBacklinksSlice';
import noteCommentsReducer from '../features/notes/noteCommentsSlice';
import apercuConceptReducer from '../features/notes/apercuConceptSlice';
import faceAFaceReducer from '../features/notes/faceAFaceSlice';
import contexteCitationReducer from '../features/notes/contexteCitationSlice';
import alignementsReducer from '../features/entrees/alignementsSlice';
import faceAFaceAvecReducer from '../features/entrees/faceAFaceAvecSlice';
import rapprochementsReducer from '../features/entrees/rapprochementsSlice';
import knowledgeGraphReducer from '../features/knowledgeGraph/knowledgeGraphSlice';
import metanotesReducer from '../features/metanotes/metanotesSlice';
import entreeReducer from '../features/entrees/entreeSlice';
import perimetreReducer from '../features/perimetres/perimetreSlice';
import thesaurusReducer from '../features/thesaurus/thesaurusSlice';
import { sharedThesaurusReducer } from '../features/sharedThesaurus';
import { sharedPerimetresReducer } from '../features/sharedPerimetres';
import { notificationsReducer, activityFeedReducer } from '../features/notifications';
import settingsReducer from '../features/settings/settingsSlice';
import textEditorReducers from '../features/texteditor/textEditorSlice';
import detectionProfileReducer from '../features/detectionProfiles/detectionProfileSlice';
import userProfileReducer from '../features/userProfile/userProfileSlice';
import ragSearchReducer from '../features/ragSearch/ragSearchSlice';
import chatReducer from '../features/chat/chatSlice';
import docReaderReducer from '../features/docReader/docReaderSlice';
import bulkJobsReducer from '../features/bulkJobs/bulkJobsSlice';
import indexationReducer from '../features/indexation/indexationSlice';
import captureQueueReducer from '../features/notes/captureQueueSlice';
import readingProgressReducer from '../features/reading/readingProgressSlice';
import premiersPasReducer from '../features/premiersPas/premiersPasSlice';
import adminReducer from '../features/admin/adminSlice';
import organisationsReducer from '../features/organisations/organisationsSlice';

export const store = configureStore({
    reducer: {
        modals: modalReducer,
        notes: noteReducer,
        noteHistory: noteHistoryReducer,
        noteBacklinks: noteBacklinksReducer,
        noteComments: noteCommentsReducer,
        apercuConcept: apercuConceptReducer,
        faceAFace: faceAFaceReducer,
        contexteCitation: contexteCitationReducer,
        alignements: alignementsReducer,
        faceAFaceAvec: faceAFaceAvecReducer,
        rapprochements: rapprochementsReducer,
        knowledgeGraph: knowledgeGraphReducer,
        metanotes: metanotesReducer,
        entrees: entreeReducer,
        perimetres: perimetreReducer,
        thesaurus: thesaurusReducer,
        sharedThesaurus: sharedThesaurusReducer,
        sharedPerimetres: sharedPerimetresReducer,
        notifications: notificationsReducer,
        activityFeed: activityFeedReducer,
        settings: settingsReducer,
        detectionProfile: detectionProfileReducer,
        userProfile: userProfileReducer,
        ragSearch: ragSearchReducer,
        chat: chatReducer,
        docReader: docReaderReducer,
        bulkJobs: bulkJobsReducer,
        indexation: indexationReducer,
        captureQueue: captureQueueReducer,
        readingProgress: readingProgressReducer,
        premiersPas: premiersPasReducer,
        admin: adminReducer,
        organisations: organisationsReducer,
        tables: require('../features/tables/tableSlice').default,
        // Reducers requis par le texteditor
        textEditor: textEditorReducers.textEditor,
    },
    middleware: (getDefaultMiddleware) =>
        getDefaultMiddleware({
            serializableCheck: false, // Désactivé comme dans texteditor-test
        }),
    devTools: process.env.NODE_ENV !== 'production',
});

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

// Hooks typés pour Redux
export const useAppDispatch = () => useDispatch<AppDispatch>();
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;
